/* eslint-disable */
import React, { useRef, useState } from "react";
import {
  Table,
  Box,
  Group,
  Text,
  HoverCard,
  Tooltip,
  Select,
} from "@mantine/core";
import { IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import styles from "./AttendanceTable.module.css";
import { useGetAttendanceAdmin } from "api/attendance/getAttendanceAdmin";
import { formatToLocalTime } from "utils/dateFormate";

const monthOptions = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 7 }, (_, i) => ({
  value: String(currentYear - 5 + i),
  label: String(currentYear - 5 + i),
}));

export default function AttendanceTable() {
  const todayRef = useRef(new Date());
  const today = todayRef.current;

  function formatDecimalHoursToHM(decimalHours: number | null | undefined) {
    if (typeof decimalHours !== "number" || !isFinite(decimalHours))
      return "N/A";

    const totalMinutes = Math.round(decimalHours * 60);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  }

  function formatDecimalHoursShort(decimalHours: number | null | undefined) {
    if (typeof decimalHours !== "number" || !isFinite(decimalHours))
      return "N/A";
    return `${decimalHours.toFixed(2)}h`;
  }

  const [month, setMonth] = useState<string | null>(
    String(today.getMonth() + 1)
  );
  const [year, setYear] = useState<string | null>(String(today.getFullYear()));

  const monthNum = month ? parseInt(month, 10) : today.getMonth() + 1;
  const yearNum = year ? parseInt(year, 10) : today.getFullYear();
  const { data, isLoading, error } = useGetAttendanceAdmin(monthNum, yearNum);

  const iconByStatus = {
    full: <IconCircleCheck color="green" size={16} />,
    absent: <IconCircleX color="red" size={16} />,
  };

  const days = Array.from(
    { length: data?.data.daysInMonth || 31 },
    (_, i) => i + 1
  );

  if (isLoading) {
    return <Text align="center">Loading...</Text>;
  }

  if (error) {
    return (
      <Text color="red" align="center">
        Error loading data
      </Text>
    );
  }

  const NOTES_MAX = 15;
  const truncate = (str: string, max: number) =>
    str.length > max ? `${str.slice(0, max)}…` : str;

  const formatDateKey = (y: number, m: number, d: number) =>
    `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <Box style={{ background: "#fff", padding: 20, borderRadius: 8 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Group spacing="xs">
            <IconCircleCheck color="green" size={16} />
            <Text size="sm">Full Day Present</Text>
          </Group>
          <Group spacing="xs">
            <IconCircleX color="red" size={16} />
            <Text size="sm">Full Day Absence</Text>
          </Group>
          <Group spacing="xs">
            <Text size="sm" color="dimmed">
              —
            </Text>
            <Text size="sm">Upcoming</Text>
          </Group>
        </div>
        <Group spacing="md" mb="md">
          <Select
            label="Month"
            data={monthOptions}
            value={month}
            onChange={setMonth}
            sx={{ width: 140 }}
          />
          <Select
            label="Year"
            data={yearOptions}
            value={year}
            onChange={setYear}
            sx={{ width: 100 }}
          />
        </Group>
      </div>

      <div className={styles.container}>
        <Table withBorder className={styles.table} striped highlightOnHover>
          <thead>
            <tr>
              <th className={styles["name-column"]}>EMPLOYEE</th>
              {days.map((day) => (
                <th
                  key={day}
                  style={{
                    textAlign: "center",
                    padding: "4px",
                  }}
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.data.attendanceData.map((emp) => (
              <tr key={emp.employee.id} style={{ width: "100%" }}>
                <td>
                  <Group spacing="xs">
                    <Text size="sm">{emp.employee.name}</Text>
                  </Group>
                </td>

                {days.map((day) => {
                  const dateKey = formatDateKey(yearNum, monthNum, day);
                  const cellDate = new Date(yearNum, monthNum - 1, day);
                  const todayDateOnly = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate()
                  );
                  const isFutureDay = cellDate > todayDateOnly;

                  const att = emp.attendance?.[dateKey] ?? {
                    status: isFutureDay ? "upcoming" : "absent",
                    workingHours: 0,
                    notes: "",
                    recordId: null,
                    activities: [],
                  };

                  const workingHours =
                    typeof att.workingHours === "number" ? att.workingHours : 0;

                  const icon = isFutureDay
                    ? null
                    : workingHours > 0
                      ? iconByStatus.full
                      : iconByStatus.absent;

                  const hasDetails =
                    att &&
                    ((att.activities && att.activities.length > 0) ||
                      !!att.notes);

                  return (
                    <td
                      key={`${emp.employee.id}-${day}`}
                      style={{ textAlign: "center", padding: "4px" }}
                    >
                      {hasDetails ? (
                        <HoverCard
                          width={300}
                          position="bottom"
                          withArrow
                          shadow="md"
                          openDelay={100}
                          closeDelay={150}
                        >
                          <HoverCard.Target>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  cursor: "pointer",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  flexDirection: "column",
                                }}
                              >
                                {icon}{" "}
                                <Text size="xs" component="span">
                                  {Math.floor(workingHours)}h
                                </Text>
                              </span>
                            </div>
                          </HoverCard.Target>
                          <HoverCard.Dropdown
                            style={{
                              maxHeight: 200,
                              overflowY: "auto",
                              overflowX: "hidden",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            <Text size="sm" fw={500} mb={4}>
                              Working Hours:{" "}
                              <span
                                style={{
                                  fontWeight: "normal",
                                  fontSize: "12px",
                                }}
                              >
                                {formatDecimalHoursToHM(workingHours)} (
                                {formatDecimalHoursShort(workingHours)})
                              </span>
                            </Text>

                            {att.notes && (
                              <Tooltip
                                label={att.notes}
                                withArrow
                                withinPortal
                                zIndex={9999}
                                disabled={att.notes.length <= NOTES_MAX}
                                position="top"
                                openDelay={200}
                              >
                                <Text
                                  size="xs"
                                  mb={6}
                                  style={{
                                    cursor:
                                      att.notes.length > NOTES_MAX
                                        ? "pointer"
                                        : "default",
                                  }}
                                >
                                  <strong
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Note:
                                  </strong>{" "}
                                  {truncate(att.notes, NOTES_MAX)}
                                </Text>
                              </Tooltip>
                            )}

                            <Text size="sm" fw={500} mb={6}>
                              Activities Table
                            </Text>

                            {att.activities.length ? (
                              <Table
                                withColumnBorders
                                striped
                                highlightOnHover
                                verticalSpacing="xs"
                              >
                                <thead>
                                  <tr>
                                    <th style={{ fontSize: "11px" }}>
                                      Clock In
                                    </th>
                                    <th style={{ fontSize: "11px" }}>
                                      Clock Out
                                    </th>
                                    <th style={{ fontSize: "11px" }}>
                                      Total Hours
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {att.activities.map((act) => (
                                    <tr
                                      key={`${act.punchInTime}-${act.workingHours}`}
                                    >
                                      <td style={{ fontSize: "11px" }}>
                                        {act?.punchInTime
                                          ? formatToLocalTime(act?.punchInTime)
                                          : "N/A"}
                                      </td>
                                      <td style={{ fontSize: "11px" }}>
                                        {act?.punchOutTime
                                          ? formatToLocalTime(act?.punchOutTime)
                                          : "N/A"}
                                      </td>
                                      <td style={{ fontSize: "11px" }}>
                                        {formatDecimalHoursToHM(
                                          act.workingHours
                                        )}
                                        h
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            ) : (
                              <Text size="xs" color="dimmed">
                                No activity data
                              </Text>
                            )}
                          </HoverCard.Dropdown>
                        </HoverCard>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "column",
                          }}
                        >
                          <span
                            style={{
                              paddingBottom: "4px",
                              display: "flex",
                              alignItems: "center",
                              flexDirection: "column",
                            }}
                          >
                            {isFutureDay ? (
                              <Text size="xs" color="dimmed">
                                —
                              </Text>
                            ) : (
                              icon
                            )}
                          </span>{" "}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Box>
  );
}
